import { h, type Component } from '#imports';
import type { Element, Root } from 'hast';
import rehypeParse from 'rehype-parse';
import { unified } from 'unified';
import type { CmsContentImageRenderer } from '@nuxt-shared/ui/cms-content';
import type { PublicationImageItem } from './types';

const parser = unified().use(rehypeParse, { fragment: true });
const IMAGE_FILE_NAME_PATTERN = /\.(?:png|jpe?g|gif|webp)$/i;

/** Проверяет, содержит ли HTML хотя бы одно изображение. */
export function hasPublicationImage(html: string): boolean {
  return !!html && !!findImage(parser.parse(html) as Root);
}

/** Создаёт рендер обычного `<img>` из HTML публикации в блок из макета. */
export function createPublicationImageRenderer(
  ImageComponent: Component,
): CmsContentImageRenderer {
  return (element: Element) => {
    const item = toPublicationImage(element);
    return item ? h(ImageComponent, { item }) : undefined;
  };
}

function toPublicationImage(element: Element): PublicationImageItem | null {
  const path = stringProperty(element, 'src');
  if (!path) return null;

  const alt = stringProperty(element, 'alt');

  return {
    path,
    alt,
    caption: getImageCaption(alt, path),
  };
}

function getImageCaption(alt: string, path: string): string {
  if (!alt || IMAGE_FILE_NAME_PATTERN.test(alt)) return '';
  return alt === getFileName(path) ? '' : alt;
}

function getFileName(path: string): string {
  const cleanPath = path.split(/[?#]/, 1)[0] || '';
  const fileName = cleanPath.split('/').at(-1) || '';

  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
}

function stringProperty(element: Element, name: string): string {
  const value = element.properties?.[name];
  return typeof value === 'string' ? value.trim() : '';
}

function findImage(node: Root | Element): Element | null {
  for (const child of node.children) {
    if (child.type !== 'element') continue;
    if (child.tagName === 'img') return child;

    const nestedImage = findImage(child);
    if (nestedImage) return nestedImage;
  }

  return null;
}
